// @file The store for AuthLibraryVerificationApp — collects a workspace (library) slug and resolves it via the auth API into a library-scoped login URL.
import { appendPkceParams, type PkceParams } from '@@/bits/auth_redirect_urls'
import { __ } from '@@/bits/intl'
import { navigateTo, transformCurrentUrl } from '@@/bits/location'
import { HttpCode } from '@@/enums'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface FormJsonResponse {
  redirect_to: string
}

const useAuthLibraryVerificationStore = defineStore('authLibraryVerificationStore', () => {
  function initialize(startingState: Partial<PkceParams>): void {
    codeChallenge.value = startingState?.codeChallenge ?? null
    originAppClientId.value = startingState?.originAppClientId ?? null
    pkceState.value = startingState?.pkceState ?? null
    originAppRedirectUri.value = startingState?.originAppRedirectUri ?? null
    codeChallengeMethod.value = startingState?.codeChallengeMethod ?? null
  }

  // State
  const slug = ref('')
  const slugValidationMessage = ref('')
  const isAwaitingServerResponse = ref(false)
  const codeChallenge = ref<string | null>(null)
  const originAppClientId = ref<string | null>(null)
  const pkceState = ref<string | null>(null)
  const originAppRedirectUri = ref<string | null>(null)
  const codeChallengeMethod = ref<string | null>(null)

  // Getters
  const isPkceLogin = computed(
    () =>
      codeChallenge.value != null &&
      codeChallengeMethod.value != null &&
      pkceState.value != null &&
      originAppClientId.value != null &&
      originAppRedirectUri.value != null,
  )

  const pkceParams = computed<PkceParams | null>(() =>
    isPkceLogin.value
      ? {
          codeChallenge: codeChallenge.value,
          codeChallengeMethod: codeChallengeMethod.value,
          pkceState: pkceState.value,
          originAppClientId: originAppClientId.value,
          originAppRedirectUri: originAppRedirectUri.value,
        }
      : null,
  )

  // Actions
  function setSlug(value: string): void {
    slug.value = value
  }

  function setSlugValidationMessage(message: string): void {
    slugValidationMessage.value = message
  }

  async function verifySlug(): Promise<void> {
    if (slug.value === '') {
      setSlugValidationMessage(__('Please fill out this field.'))
      return
    }

    setSlugValidationMessage('')
    isAwaitingServerResponse.value = true

    try {
      const url = transformCurrentUrl(
        {},
        {
          path: '/api/auth/library_login_redirect_verify',
          search: { slug: slug.value },
        },
      )
      const response: FormJsonResponse = await fetchJson(url, {
        method: HTTPMethod.get,
      })
      navigateTo(appendPkceParams(response.redirect_to, pkceParams.value))
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        // Server may return non-JSON (HTML error page, plain text) — guard against parse failure.
        let parsedErrorMessage: { error?: string } = {}
        try {
          parsedErrorMessage = JSON.parse(e.message)
        } catch {
          // fall through to generic message below
        }
        setSlugValidationMessage(parsedErrorMessage.error ?? __('Something went wrong. Please try again.'))
      } else if (e.status === HttpCode.RateLimitError) {
        setSlugValidationMessage(__('Our servers are overloaded. Please try again in a minute.'))
      } else if (e.status >= HttpCode.ServerError) {
        setSlugValidationMessage(__('Looks like our servers are down. Please try again in a bit.'))
      } else {
        setSlugValidationMessage(__('Something went wrong. Please try again.'))
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  return {
    // State
    slug,
    slugValidationMessage,
    isAwaitingServerResponse,
    codeChallenge,
    originAppClientId,
    pkceState,
    originAppRedirectUri,
    codeChallengeMethod,

    // Getters
    isPkceLogin,
    pkceParams,

    // Actions
    setSlug,
    setSlugValidationMessage,
    verifySlug,
    initialize,
  }
})
export { useAuthLibraryVerificationStore }
